import React, { useRef } from 'react';
import { Box, Tooltip } from '@chakra-ui/react';
import { FaCamera } from 'react-icons/fa';
import { useUserStore } from '../store/userStore';
import { getSignedUrl } from '../lib/api/apiClient';
import { useModernToast } from './ModernToast';

interface UserAvatarProps {
  user: {
    firstName?: string;
    lastName?: string;
    username?: string;
    avatar?: string;
    email?: string;
    status?: 'active' | 'inactive' | 'pending';
  };
  size?: 'sm' | 'md' | 'lg' | 'xl';
  canEdit?: boolean;
}

const AVATAR_SIZES = {
  sm: 32,
  md: 48,
  lg: 80,
  xl: 96,
};

const AVATAR_FONT_SIZES = {
  sm: 16,
  md: 24,
  lg: 36,
  xl: 48,
};

const AVATAR_COLORS = [
  '#d62424', // red
  '#22a000', // green
  '#a024d6', // purple
  '#2456d6', // blue
  '#24d6c6', // teal
  '#d6a024', // gold
];

function getInitials(user: UserAvatarProps['user']) {
  if (!user) return '??';
  if (user.firstName && user.lastName) {
    return `${user.firstName[0]}${user.lastName[0]}`.toUpperCase();
  }
  if (user.firstName) {
    return user.firstName.substring(0, 2).toUpperCase();
  }
  if (user.lastName) {
    return user.lastName.substring(0, 2).toUpperCase();
  }
  if (user.username) {
    return user.username.substring(0, 2).toUpperCase();
  }
  return '??';
}

function getColorIndex(str?: string) {
  if (!str) return 0;
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  return Math.abs(hash) % AVATAR_COLORS.length;
}

const UserAvatar: React.FC<UserAvatarProps> = ({ user, size = 'md', canEdit = true }) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { updateUser } = useUserStore();
  const toast = useModernToast();
  const initials = getInitials(user);
  let userId = undefined;
  try {
    const userData = localStorage.getItem('user');
    if (userData) {
      const parsed = JSON.parse(userData);
      userId = parsed.id || parsed.user_id;
    }
  } catch { /* ignore JSON parse errors */ }
  const colorIdx = !user ? 0 : getColorIndex(userId || user.username);
  const boxSize = AVATAR_SIZES[size] || AVATAR_SIZES.md;
  const fontSize = AVATAR_FONT_SIZES[size] || 24;

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Error', 'Please select an image file');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Error', 'Image size should be less than 5MB');
      return;
    }

    try {
      // Get signed URL
      const { uploadUrl, key } = await getSignedUrl(file.name);

      // Upload file to S3
      await fetch(uploadUrl, {
        method: 'PUT',
        body: file,
        headers: {
          'Content-Type': file.type,
        },
      });

      // Update user profile with new avatar URL
      await updateUser({ ...user, avatar: key, });

      toast.success('Success', 'Profile picture updated successfully');
    } catch (error) {
      console.error('Failed to upload avatar:', error);
      toast.error('Error', 'Failed to upload profile picture. Please try again.');
    }
  };

  const displayName = user?.username || user?.firstName || user?.email || 'Unknown User';

  return (
    <Box position="relative" display="inline-block">
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileChange}
        accept="image/*"
        style={{ display: 'none' }}
      />
      <Tooltip label={displayName} placement="top" hasArrow>
        <Box
          w={`${boxSize}px`}
          h={`${boxSize}px`}
          minW={`${boxSize}px`}
          minH={`${boxSize}px`}
          borderRadius="full"
          bg={AVATAR_COLORS[colorIdx]}
          display="flex"
          alignItems="center"
          justifyContent="center"
          fontWeight={400}
          fontFamily="Inter, Arial, sans-serif"
          fontSize={`${fontSize}px`}
          color="white"
          userSelect="none"
          overflow="hidden"
          cursor="pointer"
          onClick={() => canEdit && fileInputRef.current?.click()}
          position="relative"
          _hover={canEdit ? {
            '&::after': {
              opacity: 1,
            },
            '& .edit-icon': {
              opacity: 1,
            },
          } : {}}
          _after={{
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            bg: 'blackAlpha.600',
            borderRadius: 'full',
            opacity: 0,
            transition: 'opacity 0.2s',
          }}
        >
        {user?.avatar ? (
          <img
            src={`https://dominus-static-resources.s3.us-east-1.amazonaws.com/${user.avatar}`}
            alt="Profile"
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
            }}
          />
        ) : (
          initials
        )}
        <Box
          position="absolute"
          top="50%"
          left="50%"
          transform="translate(-50%, -50%)"
          zIndex={1}
          opacity={0}
          transition="opacity 0.2s"
          className="edit-icon"
        >
          <FaCamera size={boxSize * 0.3} />
        </Box>
      </Box>
      </Tooltip>
    </Box>
  );
};

export default UserAvatar; 